/** Mirrors backend systemregister.enums — keep in sync */

export const DEPLOYMENT_STATUS = [
  'PLANNED',
  'DEVELOPMENT',
  'TESTING',
  'PILOT',
  'PRODUCTION',
  'RETIRED',
  'SUSPENDED',
];

export const DECISION_TYPE = ['ADVISORY', 'SEMI_AUTOMATED', 'FULLY_AUTOMATED'];
export const APPROVAL_STATUS = ['APPROVED', 'CONDITIONAL', 'REJECTED'];
export const APPROVAL_LEVEL = ['TECHNICAL', 'COMPLIANCE', 'BUSINESS', 'EXECUTIVE'];
export const INCIDENT_SEVERITY = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
export const RESOLUTION_STATUS = ['OPEN', 'IN_PROGRESS', 'RESOLVED', 'CLOSED', 'MONITORING'];
export const CHANGE_TYPE = ['STANDARD', 'NORMAL', 'EMERGENCY'];
export const CHANGE_PRIORITY = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
export const CHANGE_RISK_LEVEL = ['LOW', 'MEDIUM', 'HIGH'];
export const IMPLEMENTATION_STATUS = [
  'PLANNED',
  'IN_PROGRESS',
  'COMPLETED',
  'FAILED',
  'ROLLED_BACK',
];
export const DOCUMENT_TYPE = ['MODEL_CARD', 'AUDIT_LOGS', 'POLICIES', 'BUSINESS_CASE', 'IT_DOCUMENT', 'SOP'];
/** Document types shown in Add / Edit IT Documents. */
export const IT_DOCUMENT_TYPE = ['IT_DOCUMENT', 'SOP', 'POLICIES', 'BUSINESS_CASE'];
export const MODEL_TYPE = ['MACHINE_LEARNING', 'DEEP_LEARNING', 'LLM', 'RULES_BASED', 'OTHER'];
export const REGULATION_TYPE = [
  'GDPR',
  'EU_AI_ACT',
  'EEOC',
  'HIPAA',
  'FINANCIAL_REGULATIONS',
  'OTHER',
];
export const SENSITIVE_DATA_CATEGORY = [
  'HEALTH',
  'BIOMETRIC',
  'FINANCIAL',
  'EMPLOYMENT',
  'NONE',
];
export const TRAINING_DATA_ORIGIN = ['INTERNAL', 'VENDOR', 'PUBLIC', 'MIXED'];

export const CONTRACT_DURATION_UNIT = ['DAYS', 'WEEKS', 'MONTHS', 'YEARS'];

export const DEPLOYMENT_ENVIRONMENT = ['CLOUD', 'ON_PREMISES', 'HYBRID'];

export const MONITORING_TYPE = [
  'PERFORMANCE_MONITORING',
  'DRIFT_MONITORING',
  'SECURITY_MONITORING',
  'COMPLIANCE_MONITORING',
  'DATA_QUALITY_MONITORING',
  'BIAS_MONITORING',
  'AVAILABILITY_MONITORING',
  'OTHER',
];
export const REVIEW_FREQUENCY = ['WEEKLY', 'MONTHLY', 'QUARTERLY', 'SEMI_ANNUAL', 'ANNUAL', 'AD_HOC'];
export const MONITORING_RISK_RATING = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
export const MONITORING_REVIEW_STATUS = ['OPEN', 'IN_PROGRESS', 'COMPLETED', 'OVERDUE'];

export const SOFTWARE_RISK_PRIORITY = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
export const SOFTWARE_RISK_STATUS = ['OPEN', 'IN_PROGRESS', 'MITIGATED', 'ACCEPTED', 'CLOSED'];
export const ASSET_STATUS = [
  'IN_USE',
  'IN_STORAGE',
  'UNDER_MAINTENANCE',
  'RETIRED',
  'DISPOSED',
  'LOST',
];
export const ASSET_CONDITION = ['EXCELLENT', 'GOOD', 'FAIR', 'POOR', 'DAMAGED'];
export const ACCESS_REQUEST_TYPE = [
  'NEW_ACCESS',
  'ACCESS_MODIFICATION',
  'ACCESS_REMOVAL',
];
export const ACCESS_PROVISION_STATUS = [
  'PENDING',
  'APPROVED',
  'REJECTED',
  'COMPLETED',
];
export const ASSET_TYPE = [
  'Laptop',
  'Desktop',
  'Monitor',
  'Server',
  'Network Device',
  'Printer',
  'Mobile Phone',
  'Tablet',
  'Peripheral',
  'Other',
];
export const INTEGRATION_TYPE = [
  'API_INTEGRATION',
  'SYSTEM_TO_SYSTEM',
  'NETWORK_INTEGRATION',
  'AUTHENTICATION_INTEGRATION',
  'DATABASE_INTEGRATION',
  'FILE_TRANSFER',
];
export const DATA_FLOW_DIRECTION = ['INBOUND', 'OUTBOUND', 'BIDIRECTIONAL'];
export const INTEGRATION_STATUS = ['ACTIVE', 'INACTIVE', 'UNDER_DEVELOPMENT', 'DISCONTINUED'];
export const INTEGRATION_METHOD_OPTIONS = [
  'REST API',
  'LDAP',
  'LDAPS',
  'RADIUS',
  'SAML',
  'IPsec',
  'SSL/TLS',
  'SFTP',
  'SQL',
];
export const AUTHENTICATION_METHOD_OPTIONS = [
  'API Key',
  'OAuth',
  'Service Account',
  'Certificates',
  'Username/Password',
];

export const SOFTWARE_RISK_CATEGORY = [
  'OPERATIONAL',
  'TECHNICAL',
  'SECURITY',
  'COMPLIANCE',
  'FINANCIAL',
  'STRATEGIC',
  'REPUTATIONAL',
  'OTHER',
];

const ENUM_LABEL_OVERRIDES = {
  MACHINE_LEARNING: 'ML',
  RULES_BASED: 'Rules-based',
  SEMI_AUTOMATED: 'Semi-automated',
  FULLY_AUTOMATED: 'Fully automated',
  FINANCIAL_REGULATIONS: 'Financial regs',
  PERFORMANCE_MONITORING: 'Performance monitoring in place',
  DRIFT_MONITORING: 'Drift monitoring',
  SECURITY_MONITORING: 'Security monitoring',
  COMPLIANCE_MONITORING: 'Compliance monitoring',
  DATA_QUALITY_MONITORING: 'Data quality monitoring',
  BIAS_MONITORING: 'Bias monitoring',
  AVAILABILITY_MONITORING: 'Availability monitoring',
  OTHER: 'Other',
  SEMI_ANNUAL: 'Semi-annual',
  AD_HOC: 'Ad hoc',
  IN_PROGRESS: 'In progress',
  NEW_ACCESS: 'New Access',
  ACCESS_MODIFICATION: 'Access Modification',
  ACCESS_REMOVAL: 'Access Removal',
  IT_DOCUMENT: 'IT Document',
  SOP: 'SOP',
  AUDIT_LOGS: 'Audit Logs',
  MODEL_CARD: 'Model Card',
  DAYS: 'Days',
  WEEKS: 'Weeks',
  MONTHS: 'Months',
  YEARS: 'Years',
  CLOUD: 'Cloud',
  ON_PREMISES: 'On-Premises',
  HYBRID: 'Hybrid',
  OPERATIONAL: 'Operational',
  TECHNICAL: 'Technical',
  SECURITY: 'Security',
  COMPLIANCE: 'Compliance',
  FINANCIAL: 'Financial',
  STRATEGIC: 'Strategic',
  REPUTATIONAL: 'Reputational',
  MITIGATED: 'Mitigated',
  ACCEPTED: 'Accepted',
  API_INTEGRATION: 'API Integration',
  SYSTEM_TO_SYSTEM: 'System-to-System',
  NETWORK_INTEGRATION: 'Network Integration',
  AUTHENTICATION_INTEGRATION: 'Authentication Integration',
  DATABASE_INTEGRATION: 'Database Integration',
  FILE_TRANSFER: 'File Transfer',
  INBOUND: 'Inbound',
  OUTBOUND: 'Outbound',
  BIDIRECTIONAL: 'Bidirectional',
  UNDER_DEVELOPMENT: 'Under Development',
  DISCONTINUED: 'Discontinued',
};

export const formatEnumLabel = (value) => {
  if (value == null || value === '') return '';
  if (ENUM_LABEL_OVERRIDES[value]) return ENUM_LABEL_OVERRIDES[value];
  return String(value)
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};
